import { useEffect, useMemo, useState } from "react";
import { listServiceOfferingDocuments } from "@/lib/service/offeringDocumentsClient";
import type { ServiceOfferingDocSummary } from "./serviceSetupReadiness";

const EMPTY_SUMMARY: ServiceOfferingDocSummary = { docCount: 0, hasOrderForm: false };

export function useServiceOfferingDocSummaries(
  offeringIds: string[],
  enabled: boolean,
  navTick: number
): Record<string, ServiceOfferingDocSummary> {
  const idKey = useMemo(() => offeringIds.slice().sort().join(","), [offeringIds]);
  const [summaries, setSummaries] = useState<Record<string, ServiceOfferingDocSummary>>({});

  useEffect(() => {
    if (!enabled || offeringIds.length === 0) {
      setSummaries({});
      return;
    }
    let cancelled = false;
    void (async () => {
      const rows = await Promise.all(
        offeringIds.map(async (id) => {
          const res = await listServiceOfferingDocuments({ offering_id: id, sign: false });
          if (!res.ok) return { id, ...EMPTY_SUMMARY };
          const docs = res.data.documents;
          return {
            id,
            docCount: docs.length,
            hasOrderForm: docs.some((d) => d.kind === "order_form"),
          };
        })
      );
      if (cancelled) return;
      setSummaries(
        Object.fromEntries(rows.map((row) => [row.id, { docCount: row.docCount, hasOrderForm: row.hasOrderForm }]))
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, idKey, navTick, offeringIds]);

  return summaries;
}
